import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { functions, db } from './firebase.js';
import {
  DASHBOARD_CARRIERS,
  DEFAULT_CARRIER_META,
  carrierMetaFor,
  lastDeliveryByCarrier,
} from './mailboxUtils.js';

function HeaderMailStrip() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const getMailboxHistory = httpsCallable(functions, 'getMailboxHistory');
        const result = await getMailboxHistory({ days: 90 });
        if (!active) return;
        const history = result.data?.history || [];
        const carrierMeta = result.data?.carrierMeta || DEFAULT_CARRIER_META;
        setSummary({
          carrierMeta,
          latest: lastDeliveryByCarrier(history, DASHBOARD_CARRIERS),
        });
      } catch (err) {
        console.error('Header mail strip error:', err);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    const interval = setInterval(load, 5 * 60 * 1000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, []);

  if (loading) {
    return <span className="header-mail-strip-loading">Mail…</span>;
  }
  if (!summary) return null;

  return (
    <Link to="/mailbox" className="header-mail-strip" title="View mail & package delivery">
      <span className="header-mail-strip-label">Mail</span>
      <div className="header-mail-strip-chips">
        {DASHBOARD_CARRIERS.map((carrierId) => {
          const meta = carrierMetaFor(carrierId, summary.carrierMeta);
          const last = summary.latest[carrierId];
          return (
            <span
              key={carrierId}
              className="header-mail-chip"
              style={{ borderColor: meta.color, background: meta.bg }}
            >
              <span className="header-mail-chip-carrier" style={{ color: meta.color }}>
                {meta.shortLabel}
              </span>
              <span className="header-mail-chip-date">
                {last ? last.dateLabel : '—'}
              </span>
            </span>
          );
        })}
      </div>
    </Link>
  );
}

function HeaderGateButton({ user }) {
  const [opening, setOpening] = useState(false);
  const [defaultLotId, setDefaultLotId] = useState('');

  useEffect(() => {
    if (!user) return undefined;
    let active = true;
    (async () => {
      try {
        const prefSnap = await getDoc(doc(db, 'user_preferences', user.uid));
        const savedLot = prefSnap.exists() ? prefSnap.data().defaultGateLotId : '';
        if (savedLot) {
          if (active) setDefaultLotId(savedLot);
          return;
        }
        const getGatePlates = httpsCallable(functions, 'getGatePlates');
        const result = await getGatePlates();
        const lots = result.data?.lots || [];
        if (active && lots.length) setDefaultLotId(lots[0].lotId);
      } catch (err) {
        console.error('Header gate lot load error:', err);
      }
    })();
    return () => { active = false; };
  }, [user]);

  const handleOpenGate = async () => {
    setOpening(true);
    try {
      const openGateFn = httpsCallable(functions, 'openGate');
      const result = await openGateFn({ lotId: defaultLotId || undefined });
      const data = result.data || {};
      toast.success(data.message || 'Gate opened');
    } catch (err) {
      console.error('Header open gate error:', err);
      toast.error(err.message || 'Failed to open gate');
    } finally {
      setOpening(false);
    }
  };

  return (
    <button
      type="button"
      className="header-gate-button"
      onClick={handleOpenGate}
      disabled={opening}
      title="Open entry gate"
    >
      {opening ? 'Opening…' : 'Open Gate'}
    </button>
  );
}

export default function HeaderToolbar({ user, isPublic }) {
  if (isPublic || !user) return null;

  return (
    <div className="header-toolbar">
      <HeaderMailStrip />
      <HeaderGateButton user={user} />
    </div>
  );
}